import Script from 'next/script'

interface FAQItem {
  question: string
  answer: string
}

interface FAQSchemaProps {
  faqs: FAQItem[]
}

export default function FAQSchema({ faqs }: FAQSchemaProps) {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  }

  return (
    <Script
      id="faq-schema"
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(faqSchema)
      }}
    />
  )
}

// Common FAQ data for different page types
export const homepageFAQs: FAQItem[] = [
  {
    question: "How do I find verified property reviews on RentWise?",
    answer: "Use our browse feature to search verified tenant and landlord reviews by location, property type, and rating. All reviews are from real tenants who have lived in the properties."
  },
  {
    question: "Are the property reviews on RentWise authentic?",
    answer: "Yes, all reviews on RentWise are verified. We require documentation and verification before allowing users to submit reviews, ensuring authenticity."
  },
  {
    question: "Can I list my property for rent or sale on RentWise?",
    answer: "Yes, landlords can list properties for rent or sale. We provide tools to help you reach verified tenants and manage your property listings effectively."
  },
  {
    question: "How does RentWise verify tenant reviews?",
    answer: "We verify tenant reviews through documentation checks, tenancy agreements, and identity verification to ensure only genuine reviews from actual tenants."
  }
]

export const browseFAQs: FAQItem[] = [
  {
    question: "How do I filter property reviews by location?",
    answer: "Use the search filters to narrow down reviews by country, state, city, and property type. You can also search by specific neighborhoods or areas."
  },
  {
    question: "What types of properties can I find reviews for?",
    answer: "You can find reviews for various property types including apartments, houses, shared accommodations, student housing, and commercial properties."
  },
  {
    question: "How recent are the property reviews?",
    answer: "Property reviews range from recent to historical, with timestamps showing when each review was submitted. Recent reviews are prioritized in search results."
  }
]

export const listingsFAQs: FAQItem[] = [
  {
    question: "How often are new listings added?",
    answer: "New property listings are added daily. We work with verified landlords and property managers to ensure fresh inventory."
  },
  {
    question: "Are these listings available for immediate viewing?",
    answer: "Most listings are available for viewing. Contact the landlord directly or request a viewing through our platform."
  },
  {
    question: "Do you charge fees for accessing listings?",
    answer: "Basic browsing is free. Premium features and detailed contact information may require a subscription."
  },
  {
    question: "How can I verify if a listing is legitimate?",
    answer: "All our listings are verified. Look for the verified badge and check property reviews from previous tenants."
  }
]

export const tenantsFAQs: FAQItem[] = [
  {
    question: "How do I start looking for properties as a tenant?",
    answer: "Create a free account, complete your profile, and start browsing verified properties with real tenant reviews."
  },
  {
    question: "Can I leave reviews for properties I've rented?",
    answer: "Yes! We encourage tenants to share honest reviews to help future renters make informed decisions."
  },
  {
    question: "Is my personal information secure on RentWise?",
    answer: "Absolutely. We use industry-standard encryption and never share your personal details without your consent."
  },
  {
    question: "How do I contact landlords about properties?",
    answer: "Use our secure messaging system to contact verified landlords directly through the platform."
  }
]

export const landlordsFAQs: FAQItem[] = [
  {
    question: "How do I list my property on RentWise?",
    answer: "Create a landlord account, verify your identity, and follow our simple property listing process with photos and details."
  },
  {
    question: "Do you charge fees for property listings?",
    answer: "We offer both free and premium listing options. Premium listings get higher visibility and additional features."
  },
  {
    question: "How do you verify tenant reviews?",
    answer: "We verify tenant identities and rental history before allowing reviews to ensure authenticity and fairness."
  },
  {
    question: "Can I respond to tenant reviews?",
    answer: "Yes, landlords can respond to reviews professionally through our platform to provide context or clarification."
  }
]

export const rentListingsFAQs: FAQItem[] = [
  {
    question: "What documents do I need to rent a property?",
    answer: "Typically you'll need ID, proof of income, references, and a deposit. Specific requirements vary by landlord."
  },
  {
    question: "Are utilities included in the rent price?",
    answer: "This varies by property. Check the listing details or contact the landlord for specific utility arrangements."
  },
  {
    question: "How long are typical lease agreements?",
    answer: "Most rentals offer 6-12 month leases, though some landlords offer flexible short-term or long-term options."
  },
  {
    question: "Can I negotiate the rent price?",
    answer: "Many landlords are open to negotiation, especially for longer lease terms or if you're a qualified tenant."
  }
]

export const saleListingsFAQs: FAQItem[] = [
  {
    question: "Are property sale prices negotiable?",
    answer: "Yes, most property prices are negotiable. We recommend researching comparable properties before making an offer."
  },
  {
    question: "Do you provide property inspection services?",
    answer: "We connect you with certified inspection services and provide property history from our database."
  },
  {
    question: "What financing options are available?",
    answer: "We partner with mortgage providers to offer competitive financing options for qualified buyers."
  },
  {
    question: "Are there any hidden fees when buying?",
    answer: "All fees are transparent. You'll see legal fees, inspection costs, and other charges clearly outlined before purchase."
  }
] 